import * as THREE from 'three';
import { Entity } from '../../entities';
import { getFaction, defaultSelection } from '../factions';
import type { FactionSelection } from '../factions';
import { buildShipSkin } from './skins/ShipSkin';
import type { ShipSkin } from './skins/ShipSkin';
import {
  animateShipVisuals,
  buildShipMesh,
  enhanceEngineFlames
} from './shipVisuals';
import type { EngineMount } from './variants';

export type { EngineMount } from './variants';

export interface PlayerShipOptions {
  readonly selection?: FactionSelection;
}

export class PlayerShip extends Entity {
  readonly engines: readonly EngineMount[];
  readonly velocity = new THREE.Vector3();
  readonly collisionRadius: number;
  readonly collisionStatic = false;
  readonly collisionRestitution = 0.15;
  readonly selection: FactionSelection;
  readonly skin: ShipSkin;

  private readonly strobes: THREE.Mesh[];
  private time = 0;
  private thrust = 0;

  constructor(options: PlayerShipOptions = {}) {
    super();
    this.selection = options.selection ?? defaultSelection();
    const faction = getFaction(this.selection.factionId);
    this.skin = buildShipSkin(faction, this.selection.primaryColor);

    this.object3D.name = `PlayerShip:${this.selection.factionId}`;

    const result = buildShipMesh(
      this.object3D,
      this.selection.factionId,
      this.selection.primaryColor
    );
    this.engines = result.engines;
    this.strobes = result.strobes;
    this.collisionRadius = result.collisionRadius;
    enhanceEngineFlames(
      this.engines,
      this.skin.plasma,
      this.skin.glow
    );
  }

  setThrust(level: number): void {
    this.thrust = THREE.MathUtils.clamp(level, 0, 1);
  }

  getThrust(): number {
    return this.thrust;
  }

  getCollisionCenter(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.object3D.position);
  }

  override update(dt: number): void {
    this.time += dt;
    animateShipVisuals(this.time, this.thrust, this.engines, this.strobes);
  }
}
